import {
  ContentBlock,
  ContentState,
  EditorState,
  genKey,
  Modifier,
  RichUtils,
  SelectionState,
} from "draft-js";
import TableOfContents from "./components/Widgets/TableOfContents";

export const toggleBlockType = (
  editorState: EditorState,
  blockType: string,
): EditorState => {
  return RichUtils.toggleBlockType(editorState, blockType);
};

export const toggleInlineStyle = (
  editorState: EditorState,
  style: string,
): EditorState => {
  return RichUtils.toggleInlineStyle(editorState, style);
};

export const addLink = (editorState: EditorState, url: string): EditorState => {
  const selection = editorState.getSelection();
  if (!url || selection.isCollapsed()) {
    return editorState;
  }

  const contentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", {
    url,
  });
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const newEditorState = EditorState.set(editorState, {
    currentContent: contentStateWithEntity,
  });

  return RichUtils.toggleLink(newEditorState, selection, entityKey);
};

export const insertCustomBlock = (
  editorState: EditorState,
  CUSTOM_BLOCK_TYPE: string,
): EditorState => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();

  const finalContentState = Modifier.setBlockType(
    contentState,
    selectionState,
    CUSTOM_BLOCK_TYPE,
  );

  return EditorState.push(editorState, finalContentState, "change-block-type");
};

export const createBlockRendererFn = (
  editorState: EditorState,
  handleEditorChange: (newEditorState: EditorState) => void,
  CUSTOM_BLOCK_TYPE: string,
) => {
  return (contentBlock: ContentBlock) => {
    const type = contentBlock.getType();
    if (type === CUSTOM_BLOCK_TYPE) {
      return {
        component: TableOfContents,
        editable: false,
        props: {
          onDelete: (blockKey: string) => {
            const contentState = editorState.getCurrentContent();
            const blockMap = contentState.getBlockMap();

            if (!blockMap.has(blockKey)) {
              console.error(`Block with key ${blockKey} does not exist.`);
              return;
            }

            const newBlockMap = blockMap.delete(blockKey);

            const newContentState = contentState.merge({
              blockMap: newBlockMap,
            }) as ContentState;

            if (newBlockMap.isEmpty()) {
              const emptyBlock = new ContentBlock({
                key: genKey(),
                type: "unstyled",
                text: "",
              });
              const updatedBlockMap = newBlockMap.set(
                emptyBlock.getKey(),
                emptyBlock,
              );
              const updatedContentState = newContentState.merge({
                blockMap: updatedBlockMap,
              }) as ContentState;

              const newEditorState = EditorState.push(
                editorState,
                updatedContentState,
                "remove-range",
              );

              handleEditorChange(newEditorState);
              return;
            }

            const newEditorState = EditorState.push(
              editorState,
              newContentState,
              "remove-range",
            );

            const previousBlockKey =
              newContentState.getKeyBefore(blockKey) ||
              newContentState.getFirstBlock().getKey();
            const newSelection = SelectionState.createEmpty(previousBlockKey);

            const finalEditorState = EditorState.forceSelection(
              newEditorState,
              newSelection,
            );

            handleEditorChange(finalEditorState);
          },
        },
      };
    }
    return null;
  };
};

export const insertEmptyBlock = (editorState: EditorState) => {
  // Create an empty block
  const emptyBlock = new ContentBlock({
    key: genKey(),
    type: "unstyled",
    text: "",
  });

  // Get the current content and block map
  const contentState = editorState.getCurrentContent();
  const blockMap = contentState.getBlockMap();

  // Add the empty block to the block map
  const updatedBlockMap = blockMap.set(emptyBlock.getKey(), emptyBlock);

  // Merge the updated block map into the content state
  const updatedContentState = contentState.merge({
    blockMap: updatedBlockMap,
  }) as ContentState;

  // Push the updated content state to the editor state
  const newEditorState = EditorState.push(
    editorState,
    updatedContentState,
    "insert-fragment",
  );

  // Move the selection to the new empty block
  const newSelection = SelectionState.createEmpty(emptyBlock.getKey());
  const finalEditorState = EditorState.forceSelection(
    newEditorState,
    newSelection,
  );

  return finalEditorState;
};
